import type {
  FaceKey,
  FlatSurface,
  ResolvedEditAreaDefinition,
  Surface,
} from '@repo/api-contracts';
import { componentRectOnPhoto } from '@repo/design-renderer';
import type { PhotoBackdropRect } from './flat-mode-mapping';

/** 목업 위 바로 편집의 배경 — 지금 색상·면의 목업 사진(관리자가 색상×면마다 올린 사진)과 그 위 인쇄 영역 자리. */
export interface FaceBackdrop extends PhotoBackdropRect {
  readonly url: string;
  /** 곡면(cylinder·mesh) 위 배치라 편집 중에는 평면으로 겹쳐 보이는지. */
  readonly approximate: boolean;
}

interface FaceBackdropInput {
  readonly definition: ResolvedEditAreaDefinition;
  readonly colorOptionId: string;
  readonly faceKey: FaceKey;
  readonly componentId: string;
}

/** 곡면은 제어점의 외접 사각형을 평면으로 보고 겹친다 — 편집 중 빠른 길(최종 인쇄 위치는 서버 렌더가 정한다). */
export const asFlatSurface = (surface: Surface): FlatSurface => {
  if (surface.type === 'flat') return surface;
  const xs = surface.points.map((point) => point.x);
  const ys = surface.points.map((point) => point.y);
  const left = Math.min(...xs);
  const right = Math.max(...xs);
  const top = Math.min(...ys);
  const bottom = Math.max(...ys);
  return {
    id: surface.id,
    type: 'flat',
    realSize: surface.realSize,
    points: [
      { x: left, y: top },
      { x: right, y: top },
      { x: right, y: bottom },
      { x: left, y: bottom },
    ],
  };
};

/**
 * 고객 에디터가 편집 표면 뒤에 깔 목업 사진과 인쇄 영역 자리(사진 px). 상품 썸네일·대표 이미지가 아니라
 * 편집영역 정의의 색상별 면 사진(photoUrl)만 쓴다. 사진·배치가 없으면 null 이다.
 */
export const faceBackdropOf = ({
  definition,
  colorOptionId,
  faceKey,
  componentId,
}: FaceBackdropInput): FaceBackdrop | null => {
  const photo = definition.colors.find((entry) => entry.colorOptionId === colorOptionId)?.faces[
    faceKey
  ];
  const face = definition.faces[faceKey];
  const placement = face?.placements.find((candidate) => candidate.componentId === componentId);
  const surface = face?.surfaces.find((candidate) => candidate.id === placement?.surfaceId);
  const component = definition.components.find((candidate) => candidate.id === componentId);
  if (
    photo === undefined ||
    placement === undefined ||
    surface === undefined ||
    component === undefined
  )
    return null;
  return {
    url: photo.photoUrl,
    photo: photo.photoSize,
    rect: componentRectOnPhoto(
      asFlatSurface(surface),
      photo.photoSize,
      component,
      placement.position,
    ),
    approximate: surface.type !== 'flat',
  };
};
